<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">MARKET RENT WORKSHEET > RENOVATION TAB</h1>
    <span class="banner py-3 mb-4">
      <div>This tab will allow you to establish any renovation-based premiums for the budget year.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <ul>
            <li>Initially you will have an option to complete the <strong>goal sentence</strong> by adding either your desired percentage or your desired number of units and the additional Renovation Premium income you expect.</li>
            <li>If you enter the desired percentage, the system will provide the number of units. If you enter the desired number of units, the system will provide the percentage.</li>
            <li>Your total number of units will display but is not editable. This number includes Model/Admin/Down units.</li>
            <li>You will enter the dollar amount of the estimated additional income you anticipate for a renovated unit.</li>
            <li>You will have the option to calculate your new renovation income at the Property level or at the Unit Type Level.</li>
          </ul>
          <div><strong>Clear and Reset</strong> will clear out all data that has been entered on this tab.</div>

        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculation Table - Property Level</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <ul>
            <li><strong># of Unit Renovations</strong> - Initially these fields will be blank. You will enter the number of anticipated renovations for each month.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  As you add numbers to the monthly columns, the TOTAL/AVG column will display whether you are over, under, or matching your goal number of units. It shows the sum of the monthly numbers you have entered over the number in your goal statement. If you are over or under, the TOTAL/AVG column will show the number in red.
                </li>
                <li class="no-bullet">
                  <ul>
                    <li>Example: Your goal was 10 units, but you have entered 8 units. It will display 8/10.</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>Renovation Revenue</strong> - Initially it will multiply your # of Unit Renovations times your dollar amount entered in your goal statement. However, you can edit this number if desired.</li>
            <li><strong>Projected Renovation Revenue</strong> - Initially it will take any increases and project them through the future months. You can edit these numbers if desired.</li>
          </ul>
          <div>As you complete your budget year Renovation fields, they will populate in the top summary table.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculation Table - Unit Type Level</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <ul>
            <li><strong># of Unit Renovations</strong> - Initially this row will be blank. As you enter the number of anticipated renovations within each Unit Type below, this line will display the totals.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  As you add numbers to the monthly columns below, the TOTAL/AVG column will display whether you are over, under or matching your goal number of units. It shows the sum of the monthly numbers you have entered over the number in your goal statement. If you are over or under, the TOTAL/AVG column will show the number in red.
                </li>
                <li class="no-bullet">
                  <ul>
                    <li>Example: Your goal was 10 units, but you have entered 8 units. It will display 8/10.</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>Unit Type Section</strong> - Initially you will see a list of your Unit Types compressed.<br/>As you expand them, you will find the following rows:</li>
            <li><strong># of Renovations</strong> - Initially will be blank. You will enter the number of anticipated renovations for each month for this Unit Type. (Note: This row does not have a title indicating "# of Renovations".)</li>
            <li><strong>Renovation Revenue</strong> - Initially it will multiply your # of Unit Renovations times your dollar amount entered in your goal statement. However, you can edit this number if desired.</li>
            <li><strong>Projected Renovation Revenue</strong> - Initially it will take any increases and project them through the future months. You can edit these numbers if desired.</li>
          </ul>
          <div>As you complete your budget year Renovation fields, they will populate in the top summary table.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons & Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Within each of the Market Rent tabs you will have additional options on the right-side of the screen.</div>
          <img src="../../assets/help/a.png" />
          <div>
            <img class="small" src="../../assets/help/b.png" />
            <span>This icon will bring up a help screen specific to this page</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/c.png" />
            <span>This icon will open the Comments and Flags screen which will allow you to add a general comment, create a flagged comment, or reply to an existing comment. If it is empty in the middle, there are no comments or flags.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/d.png" />
            <span>If it has changed to a solid blue, then comments have been entered.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/e.png" />
            <span>If it has changed to an orange flag, then an unresolved flag or issue exists. To resolve the Flag, click into the Comments and Flags screen and select Resolve the outstanding issue. Once resolved, the icon will change back to blue.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/f.png" />
            <span>Clicking this ellipsis will bring up a new menu of options.</span>
          </div>
          <br />
          <div><strong>Add Flag or Comment</strong> will bring up the same screen as the center icon allowing you to add a comment, a flagged comment or resolve a comment</div>
          <div><strong>Clear & Reset</strong> will clear out any data that has been added and reset this tab back to the default values when the page was first loaded. This only applies to the specific tab you are on. It will also popup a message to explain what is about to happen and to allow you to confirm or cancel.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Market Rent Renovations Tab')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'MarketRentRenovationsHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
